import { SalesStatistics, UserStats } from './../../types';

// Simple normal distribution using Box-Muller
const randn = () => {
  let u = 0, v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
};
const normal = (mean: number, sd: number) => Math.max(0, mean + sd * randn());
const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const roundTo = (n: number, step: number) => Math.round(n / step) * step;

const USERS: Array<{ id: number; name: string }> = [
  { id: 57, name: 'Alejandro' },
  { id: 61, name: 'Luis' },
  { id: 64, name: 'Roberto' },
  { id: 62, name: 'Claudio' },
  { id: 66, name: 'Claudia' },
  { id: 67, name: 'Isidora' },
];

export interface GeneralSalesFakeOptions {
  days?: number; // how many daily sessions
  startDate?: Date; // starting date (defaults to today - days + 1)
  localId?: number; // not used now, reserved
}

export const generateGeneralSalesFakeData = (opts: GeneralSalesFakeOptions = {}) => {
  const days = opts.days ?? 180;
  const end = new Date();
  const start = opts.startDate ? new Date(opts.startDate) : new Date(end);
  if (!opts.startDate) start.setDate(end.getDate() - (days - 1));

  const sessions: SalesStatistics[] = [];
  let idCounter = 1000;

  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);

    const weekday = d.getDay(); // 0 Sun - 6 Sat
    // Base daily gross sales in CLP, with weekend uplift
    const baseMean = 1_000_000 * (weekday === 0 || weekday === 6 ? 1.25 : 1.0); // ~1M on weekdays, ~1.25M weekends
    const grossSales = roundTo(normal(baseMean, baseMean * 0.25), 100); // add 25% sd, round to $100

    // Discounts and coupons (1% - 5%)
    const total_discounts = roundTo(grossSales * (0.01 + Math.random() * 0.04), 100);
    const total_coupons = roundTo(grossSales * (Math.random() * 0.01), 100);

    // Losses (cancellations, wastage): 0% - 3%
    const net_loss = roundTo(grossSales * (Math.random() * 0.03), 100);
    const gross_loss = net_loss; // keep same scale for simplicity

    // Tips 5% - 10% of gross
    const total_tip = roundTo(grossSales * (0.05 + Math.random() * 0.05), 100);

    // Net sales definition: gross - discounts - coupons - net_loss
    const net_sales = clamp(grossSales - total_discounts - total_coupons - net_loss, 0, Infinity);

    // Receipts and avg ticket
    const avg_ticket = roundTo(clamp(normal(12_000, 2_500), 8_000, 20_000), 100);
    const valid_receipt_count = Math.max(1, Math.round(net_sales / avg_ticket));

    // Pending
    const pending_payments_count = Math.random() < 0.1 ? Math.floor(Math.random() * 3) : 0;
    const total_pending = pending_payments_count > 0 ? roundTo(avg_ticket * pending_payments_count * 0.8, 100) : 0;

    // Users split
    const usersToday = USERS.sort(() => 0.5 - Math.random()).slice(0, 3 + Math.floor(Math.random() * 3));
    const weights = usersToday.map(() => Math.random());
    const sumW = weights.reduce((a, b) => a + b, 0) || 1;

    const users_stats: Record<number, UserStats> = {};
    usersToday.forEach((u, idx) => {
      const portion = weights[idx] / sumW;
      const sales = roundTo(net_sales * portion, 100);
      const receipts = Math.max(1, Math.round(valid_receipt_count * portion));
      const tip = roundTo(total_tip * portion, 100);
      const cancelled_amount = roundTo(net_loss * portion, 100);
      const cancelled_receipts = cancelled_amount > 0 ? Math.max(0, Math.round(receipts * 0.05)) : 0;
      users_stats[u.id] = {
        user_id: u.id,
        name: u.name,
        total_sales: sales,
        receipt_count: receipts,
        avg_total_sales: receipts > 0 ? Math.round(sales / receipts) : sales,
        tip_total: tip,
        avg_tip: receipts > 0 ? Math.round(tip / receipts) : tip,
        cancelled_receipts,
        cancelled_amount,
        avg_cancelled_amount: cancelled_receipts > 0 ? Math.round(cancelled_amount / (cancelled_receipts || 1)) : 0,
      };
    });

    // Pedido types split
    const rWeight = 0.7 + Math.random() * 0.2; // 70% - 90%
    const dWeight = 1 - rWeight; // rest
    const type_stats: Record<number, any> = {
      1: {
        pedido_type_id: 1,
        name: 'Restaurant',
        total_sales: roundTo(net_sales * rWeight, 100),
        receipt_count: Math.max(1, Math.round(valid_receipt_count * rWeight)),
        avg_total_sales: 0, // filled below
      },
      3: {
        pedido_type_id: 3,
        name: 'Delivery',
        total_sales: roundTo(net_sales * dWeight, 100),
        receipt_count: Math.max(1, Math.round(valid_receipt_count * dWeight)),
        avg_total_sales: 0, // filled below
      },
    };
    Object.values(type_stats).forEach((t: any) => {
      t.avg_total_sales = t.receipt_count > 0 ? Math.round(t.total_sales / t.receipt_count) : t.total_sales;
    });

    const start_timestamp = new Date(d);
    start_timestamp.setHours(12, 0, 0, 0);
    const end_timestamp = new Date(d);
    end_timestamp.setHours(23, 30, 0, 0);

    const entry: SalesStatistics = {
      sales_total_id: idCounter++,
      local_session_id: 2000 + i, // fake session id
      timestamp: end_timestamp.toISOString(),
      start_timestamp: start_timestamp.toISOString(),
      end_timestamp: end_timestamp.toISOString(),
      day_number: d.getDate(),
      total_charged: roundTo(net_sales + total_tip, 100),
      gross_sales: grossSales,
      valid_receipt_count,
      users_count: Object.keys(users_stats).length,
      net_sales,
      pending_payments_count,
      total_pending,
      gross_loss,
      net_loss,
      cancelled_receipt_count: Math.round(valid_receipt_count * 0.02),
      avg_ticket,
      total_tip,
      avg_tip_by_ticket: valid_receipt_count > 0 ? Math.round(total_tip / valid_receipt_count) : 0,
      total_discounts,
      total_coupons,
      avg_gross_sales: grossSales, // daily avg proxy
      avg_net_sales: net_sales,
      avg_net_loss: net_loss,
      avg_total_tip: total_tip,
      users_stats,
      type_stats,
      user_id: null,
      user_name: null,
    };

    sessions.push(entry);
  }

  // Sort newest first to match existing component expectations
  sessions.sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));
  return sessions;
};

// Pre-generated large dataset for demos (approx 12 months)
export const generalSalesDemoData: SalesStatistics[] = generateGeneralSalesFakeData({ days: 360 });
